/**
 * The demo content arm B's product scenes are built from (#1070): who the demo
 * person is, which photo sits in which slot, and the numbers the scenes show.
 *
 * Every photo is an AI-generated demo person; no real user data appears
 * anywhere. Copy is not here: every string is in the catalog under
 * `web.homeB`, and the entries below name their catalog key. Changing a face
 * or a line is one edit here (or one in each catalog).
 *
 * The slot map is the designer's handoff (`HANDOFF.md`, "Slot → file map"),
 * identical on desktop and mobile. The images are committed derivatives under
 * {@link DEMO_IMAGE_BASE}; that folder's README records how they were made.
 */

/** The demo person's first name, as the scenes greet her. */
export const DEMO_NAME = "Maya";

/** Where the committed demo derivatives are served from. */
export const DEMO_IMAGE_BASE = "/static/reflect/homepage/demo";

/**
 * The widths each photo is committed at. The sources are 768px wide, so the
 * largest derivative is the source width rather than an upscaled 960.
 */
export const DEMO_IMAGE_WIDTHS = [240, 480, 768] as const;

/** One committed derivative width. */
export type DemoImageWidth = (typeof DEMO_IMAGE_WIDTHS)[number];

/** The formats each photo is committed in, best first. */
export const DEMO_IMAGE_FORMATS = ["avif", "webp"] as const;

/** One committed derivative format. */
export type DemoImageFormat = (typeof DEMO_IMAGE_FORMATS)[number];

/** The source photos' intrinsic size, which every derivative keeps the ratio of. */
export const DEMO_IMAGE_SIZE = { width: 768, height: 1250 } as const;

/** Every photo the scenes use, by its scene-based file name. */
export const DEMO_SCENES = [
  "cafe",
  "car-parked",
  "couch-night",
  "bedroom-low-light",
  "balcony-golden",
  "walk-outdoors",
  "desk-evening",
  "kitchen-morning",
  "desk-sweater",
  "portrait-laugh",
  "portrait-neutral",
  "chair-green",
] as const;

/** One demo photo. */
export type DemoScene = (typeof DEMO_SCENES)[number];

/**
 * The URL of one committed derivative.
 *
 * @param scene - the photo.
 * @param width - one of {@link DEMO_IMAGE_WIDTHS}.
 * @param format - one of {@link DEMO_IMAGE_FORMATS}.
 */
export function demoImageSrc(
  scene: DemoScene,
  width: DemoImageWidth,
  format: DemoImageFormat,
): string {
  return `${DEMO_IMAGE_BASE}/${scene}-${width}.${format}`;
}

/**
 * A `srcset` listing every committed width of one photo in one format.
 *
 * @param scene - the photo.
 * @param format - one of {@link DEMO_IMAGE_FORMATS}.
 */
export function demoSrcSet(scene: DemoScene, format: DemoImageFormat): string {
  return DEMO_IMAGE_WIDTHS.map((w) => `${demoImageSrc(scene, w, format)} ${w}w`).join(", ");
}

/** One thumbnail in the dashboard scene's grid. */
export interface DashboardTile {
  /** The tile's key under `web.homeB.scene.tiles`. */
  readonly id: "cafe" | "car" | "couch" | "bedroom" | "balcony" | "walk";
  /** Its photo. */
  readonly scene: DemoScene;
  /** How many grid rows it spans in the two-column grid. */
  readonly span: 1 | 2;
}

/** The dashboard grid, in reading order. */
export const DASHBOARD_TILES: readonly DashboardTile[] = [
  { id: "cafe", scene: "cafe", span: 2 },
  { id: "car", scene: "car-parked", span: 1 },
  { id: "couch", scene: "couch-night", span: 1 },
  { id: "bedroom", scene: "bedroom-low-light", span: 1 },
  { id: "balcony", scene: "balcony-golden", span: 1 },
  { id: "walk", scene: "walk-outdoors", span: 2 },
];

/** One card in the pattern deck beside (or under) the dashboard. */
export interface DeckCard {
  /** Its photo. */
  readonly scene: DemoScene;
  /** Offset from the deck's top edge, in px. */
  readonly top: number;
  /** Offset from the deck's left edge, in px. */
  readonly left: number;
  /** Rotation in degrees; negative tilts left. */
  readonly rotate: number;
}

/** The pattern deck, back to front. */
export const PATTERN_DECK: readonly DeckCard[] = [
  { scene: "desk-evening", top: 20, left: 150, rotate: 14 },
  { scene: "kitchen-morning", top: 14, left: 118, rotate: 8 },
  { scene: "desk-sweater", top: 10, left: 84, rotate: 1 },
  { scene: "portrait-laugh", top: 16, left: 40, rotate: -8 },
];

/** The figures the dashboard scene shows. */
export const DASHBOARD_STATS = {
  /** "12 reflections in view." */
  inView: 12,
  /** The count beside "Your reflections". */
  total: 133,
  /** The laptop's progress to the next pattern. */
  progressWide: { done: 1, total: 5 },
  /** The phone's progress to the next pattern. */
  progressNarrow: { done: 3, total: 5 },
} as const;

/** Which phone screen a stepper step draws. */
export type StepScreen = "prompt" | "countdown" | "recording" | "heard";

/** One step of Strip B's stepper. */
export interface StepperStep {
  /** The step's key under `web.homeB.stripB.steps`. */
  readonly id: "thread" | "breath" | "speak" | "heard";
  /** The phone screen it shows. */
  readonly screen: StepScreen;
  /** The photo behind that screen, or `null` for a drawn one. */
  readonly scene: DemoScene | null;
}

/** The record-to-insight steps, in order. */
export const STEPPER_STEPS: readonly StepperStep[] = [
  { id: "thread", screen: "prompt", scene: null },
  { id: "breath", screen: "countdown", scene: "portrait-neutral" },
  { id: "speak", screen: "recording", scene: "chair-green" },
  { id: "heard", screen: "heard", scene: null },
];

/** Figures drawn on the recording screens; numbers, not copy. */
export const RECORDING_DEMO = {
  /** The countdown digit caught mid-count. */
  countdown: "2",
  /** Elapsed and maximum recording time. */
  timer: "0:04 / 5:00",
} as const;

/* ── Stepper motion ───────────────────────────────────────────────── */

/** The two stepper layouts: below 900px vertical, at 900px and above zigzag. */
export type StepperLayout = "vertical" | "zigzag";

/** The width at which the stepper turns from vertical to zigzag. */
export const STEPPER_ZIGZAG_MIN_WIDTH = 900;

/**
 * The rail fill, in percent, at which step `index`'s dot turns solid. The
 * prototype's `reachedAt`: vertical dots sit at each step's top, zigzag dots
 * at each step's middle.
 *
 * @param index - the step's zero-based position.
 * @param layout - the layout on screen.
 */
export function stepReachedAt(index: number, layout: StepperLayout): number {
  return layout === "zigzag" ? (index + 0.5) * 25 - 4 : index * 25;
}

/**
 * How far the rail has filled, as a whole percent: the share of the stepper
 * that has scrolled past 60% of the viewport's height, clamped to 0–100.
 *
 * @param top - the stepper's top edge relative to the viewport, in px.
 * @param height - the stepper's height, in px.
 * @param viewportHeight - the viewport's height, in px.
 */
export function railProgress(top: number, height: number, viewportHeight: number): number {
  if (height <= 0) return 100;
  const share = (viewportHeight * 0.6 - top) / height;
  return Math.round(Math.max(0, Math.min(1, share)) * 100);
}
